import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { AI_SATEI_URL, COMPANY } from '@/constants/company';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const servicesJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/services#webpage`,
      url: `${siteUrl}/services`,
      name: '不動産売却・買取・仲介サービス｜名古屋 株式会社クリアコーポレーション',
      description: '名古屋の不動産売却・買取・仲介サービス。スピード買取（最短7日現金化）・売却相談・仲介の3つのサービスを提供。秘密厳守・押し売りなし。名古屋16区対応の株式会社クリアコーポレーション。',
      inLanguage: 'ja',
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/services#service-purchase`,
      name: '不動産買取',
      description: '仲介を通さず、株式会社クリアコーポレーションが直接お客様の不動産を買い取ります。最短数日での現金化も可能。',
      provider: {
        '@type': 'RealEstateAgent',
        name: '株式会社クリアコーポレーション',
        url: siteUrl,
      },
      areaServed: '名古屋市全16区・全国',
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/services#service-consultation`,
      name: '不動産売却相談',
      description: '売るかどうかまだ決めていない段階でも大丈夫。相談・査定は完全無料。押し売り一切なし。',
      provider: {
        '@type': 'RealEstateAgent',
        name: '株式会社クリアコーポレーション',
        url: siteUrl,
      },
      areaServed: '名古屋市全16区・全国',
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/services#service-brokerage`,
      name: '不動産仲介',
      description: '時間をかけてでも高値での売却を目指したい方に最適。広告掲載・内覧対応・価格交渉まで宅建業者としてサポート。',
      provider: {
        '@type': 'RealEstateAgent',
        name: '株式会社クリアコーポレーション',
        url: siteUrl,
      },
      areaServed: '名古屋市全16区・全国',
    },
  ],
};

const services = [
  {
    icon: 'ri-flashlight-line',
    tag: 'スピード重視',
    title: '買取',
    subtitle: '当社が直接買い取ります',
    desc: '仲介を通さず、株式会社クリアコーポレーションが直接お客様の不動産を買い取ります。最短数日での現金化も可能で、近所への告知も不要。手続きもシンプルで、現状のままお売りいただけます。',
    points: ['最短数日で現金化', '仲介手数料なし', '秘密厳守・近所への告知なし', '現状のまま売却可', '相続・空き家・共有持分も対応'],
    cta: '無料で相談する',
    ctaHref: '/contact#form',
    external: false,
    image: 'https://public.readdy.ai/ai/img_res/e2ab3e119a24c7f2e97ff643002a9544.jpg',
  },
  {
    icon: 'ri-chat-smile-3-line',
    tag: 'まずは相談',
    title: '売却相談',
    subtitle: '売るかどうか迷っている段階でもOK',
    desc: '「売るかどうかまだ決めていない」「相場を知りたいだけ」という段階でも大丈夫です。状況を整理するだけでも、ぜひお気軽にご相談ください。押し売りは一切しません。',
    points: ['相談・査定は完全無料', '売却時期の相談OK', '複数の選択肢を提示', '押し売り一切なし', 'オンライン・電話相談対応'],
    cta: '無料で相談する',
    ctaHref: '/contact#form',
    external: false,
    image: 'https://readdy.ai/api/search-image?query=Japanese%20business%20consultation%20meeting%20professional%20real%20estate%20agent%20friendly%20atmosphere%20office%20interior%20clean%20minimal%20design%20warm%20lighting&width=800&height=600&seq=svc002&orientation=landscape',
  },
  {
    icon: 'ri-price-tag-3-line',
    tag: '高値重視',
    title: '仲介',
    subtitle: '市場に出して最大限の価格を目指す',
    desc: '時間をかけてでも高値での売却を目指したい方に最適です。広告掲載・内覧対応・価格交渉まで、宅建業者として適切にサポートします。',
    points: ['市場価格での売却を目指す', '広告・内覧対応', '価格交渉サポート', '法的に適切な手続き保証', '売却後のアフターフォロー'],
    cta: '無料で相談する',
    ctaHref: '/contact#form',
    external: false,
    image: 'https://readdy.ai/api/search-image?query=Japanese%20residential%20property%20for%20sale%20sign%20modern%20house%20exterior%20clean%20neighborhood%20street%20view%20Nagoya%20real%20estate%20market%20professional%20photography&width=800&height=600&seq=svc003&orientation=landscape',
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen">
      <title>不動産売却・買取・仲介サービス｜名古屋 株式会社クリアコーポレーション</title>
      <meta name="description" content="名古屋の不動産売却・買取・仲介サービス。スピード買取（最短7日現金化）・売却相談・仲介の3つのサービスを提供。秘密厳守・押し売りなし。名古屋16区対応の株式会社クリアコーポレーション。" />
      <meta name="keywords" content="名古屋 不動産買取, 名古屋 不動産売却相談, 名古屋 不動産仲介, 名古屋 スピード買取" />
      <meta property="og:title" content="不動産売却・買取・仲介サービス｜名古屋 株式会社クリアコーポレーション" />
      <meta property="og:description" content="名古屋の不動産売却・買取・仲介サービス。スピード買取（最短7日現金化）・売却相談・仲介の3つのサービスを提供。秘密厳守・押し売りなし。" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/services" />
      <script type="application/ld+json">{JSON.stringify(servicesJsonLd)}</script>
      <Header />
      <main>
        {/* Page Hero */}
        <section className="pt-32 pb-16 bg-[#F8F9FB]">
          <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
            <p className="text-[#4A90E2] text-sm font-medium tracking-widest mb-3 uppercase">Services</p>
            <h1 className="text-[#1A2B4A] text-3xl md:text-5xl font-bold mb-5">サービス</h1>
            <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              買取・売却相談・仲介。状況に合わせて、最適な方法をご提案します。<br />
              まずはお気軽にご相談ください。
            </p>
          </div>
        </section>

        {/* Services */}
        {services.map((service, index) => (
          <section key={index} className={`py-20 md:py-28 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FB]'}`}>
            <div className="max-w-6xl mx-auto px-4 md:px-8">
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                <div className="w-full lg:w-1/2">
                  <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#F0F4FA] rounded-xl">
                      <i className={`${service.icon} text-[#1A2B4A] text-2xl`} />
                    </div>
                    <span className="text-xs text-[#4A90E2] border border-[#4A90E2]/30 px-3 py-1 rounded-full">{service.tag}</span>
                  </div>
                  <h2 className="text-[#1A2B4A] text-3xl font-bold mb-2">{service.title}</h2>
                  <p className="text-[#4A90E2] text-sm font-medium mb-5">{service.subtitle}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-7">{service.desc}</p>
                  <ul className="space-y-2.5 mb-8">
                    {service.points.map((point, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#4A90E2]" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  {service.external ? (
                    <a href={service.ctaHref} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-8 py-4 rounded-full font-medium text-sm whitespace-nowrap hover:bg-[#243d6b] transition-colors duration-200 cursor-pointer">
                      {service.cta} <i className="ri-arrow-right-line" />
                    </a>
                  ) : (
                    <Link to={service.ctaHref}
                      className="inline-flex items-center gap-2 border border-[#1A2B4A] text-[#1A2B4A] px-8 py-4 rounded-full font-medium text-sm whitespace-nowrap hover:bg-[#1A2B4A] hover:text-white transition-colors duration-200 cursor-pointer">
                      {service.cta} <i className="ri-arrow-right-line" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
        ))}

      </main>
      <Footer />
    </div>
  );
}
